import { useMemo } from 'react';
import {
  IntegrationProviderId,
  PropertyPortal,
  type CatalogProperty,
} from 'src/api/generated';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import {
  IntegrationPermission,
  PropertyPermission,
} from 'src/modules/shared/types/permissions.type';
import { IntegrationConnectionHealthStatus } from 'src/api/generated';
import {
  getMeliDraftIssueFieldLabelKey,
  getMeliDraftIssueMessageKey,
  getMeliDraftIssueMessageParams,
} from '../utils/meli-draft-issues.util';
import {
  buildPropertyPublicationListItems,
  getQueuedDisplayStatus,
} from '../utils/property-publishing-panel.util';
import {
  getMeliPublicationById,
  getSupportedMeliOperationFromPublication,
} from '../utils/meli-publication.util';
import { usePropertyPublishingDraftPreviews } from './use-property-publishing-draft-previews';
import { usePropertyPublishingState } from './use-property-publishing-state';

interface UsePropertyPublishingPanelParams {
  property: CatalogProperty | null;
}

interface PropertyPublishingPanelModalState {
  /** Persisted MeLi publication currently managed in the modal. */
  currentManagerPublication:
    | ReturnType<
        typeof usePropertyPublishingState
      >['publications']['publications'][number]
    | null;
  /** Operation currently opened in the modal. */
  managerOperation: ReturnType<
    typeof usePropertyPublishingState
  >['modal']['managerOperation'];
  /** Publication id currently opened in the modal, when managing a stored row. */
  managerPublicationId: ReturnType<
    typeof usePropertyPublishingState
  >['modal']['managerPublicationId'];
  /** Updates the operation currently opened in the modal. */
  setManagerOperation: ReturnType<
    typeof usePropertyPublishingState
  >['modal']['setManagerOperation'];
  /** Updates the publication currently opened in the modal. */
  setManagerPublicationId: ReturnType<
    typeof usePropertyPublishingState
  >['modal']['setManagerPublicationId'];
}

interface UsePropertyPublishingPanelResult {
  /** Integration and source-of-truth state for the current property. */
  connection: ReturnType<typeof usePropertyPublishingState>['connection'];
  /** Publication rows rendered by the publishing rail. */
  items: ReturnType<typeof buildPropertyPublicationListItems>;
  /** Whether persisted publications are still loading. */
  loading: ReturnType<typeof usePropertyPublishingState>['loading'];
  /** Modal state derived from the selected publication operation. */
  modal: PropertyPublishingPanelModalState;
  /** Persisted publication query state for the current property. */
  permissions: {
    canDeletePublications: boolean;
    canManagePublications: boolean;
    canRepairSync: boolean;
    canReadPublications: boolean;
    loadingPermissions: boolean;
  };
  /** Persisted publication query state for the current property. */
  publications: ReturnType<typeof usePropertyPublishingState>['publications'];
  /** Queued publish or unpublish state used for optimistic row feedback. */
  queued: ReturnType<typeof usePropertyPublishingState>['queued'];
  /** Direct row shortcut actions. */
  shortcuts: ReturnType<typeof usePropertyPublishingState>['shortcuts'];
  /** Property-scoped repair-sync action state. */
  repairSync: ReturnType<typeof usePropertyPublishingState>['repairSync'];
  /** Status helper function used by the panel modal header. */
  getQueuedDisplayStatus: typeof getQueuedDisplayStatus;
}

/**
 * Composes the publishing rail state from smaller hooks so the panel component
 * only renders grouped state instead of owning query and action orchestration.
 */
export const usePropertyPublishingPanel = ({
  property,
}: UsePropertyPublishingPanelParams): UsePropertyPublishingPanelResult => {
  const { t } = useI18n();
  const {
    hasPermission: canReadPublications,
    loading: loadingPublicationRead,
  } = useHasPermissions(IntegrationPermission.Read);
  const { hasPermission: canPublishAll, loading: loadingPublishAll } =
    useHasPermissions(PropertyPermission.Publish);
  const { hasPermission: canPublishScoped, loading: loadingPublishScoped } =
    useHasPermissions(PropertyPermission.PublishScoped);
  const {
    hasPermission: canRepairSourceSync,
    loading: loadingRepairSourceSyncPermission,
  } = useHasPermissions(IntegrationPermission.SyncNow);
  const { user } = useCurrentUser();
  const canManagePublications =
    canPublishAll ||
    (canPublishScoped && user != null && property?.userId === user.id);
  const canDeletePublications = canManagePublications;
  const state = usePropertyPublishingState({ property });
  const {
    managerOperation,
    managerPublicationId,
    setManagerOperation,
    setManagerPublicationId,
  } = state.modal;

  const previewDraftsEnabled =
    property != null &&
    canReadPublications &&
    state.connection.isMeliConnected &&
    !state.connection.isMeliSyncMode &&
    !state.connection.loadingIntegrations &&
    !state.loading &&
    !loadingPublicationRead;

  const previews = usePropertyPublishingDraftPreviews({
    managerOperation,
    previewDraftsEnabled,
    property,
    publications: state.publications.publications,
    operations: state.publications.operations,
  });

  const items = useMemo(
    () =>
      buildPropertyPublicationListItems({
        canDeletePublications,
        canManagePublications,
        canReadPublications,
        draftByOperation: previews.draftByOperation,
        enqueueingPublish: state.shortcuts.enqueueingPublish,
        enqueueingUnpublish: state.shortcuts.enqueueingUnpublish,
        getIssueFieldLabel: (field) => {
          const fieldLabelKey = getMeliDraftIssueFieldLabelKey(field);
          return fieldLabelKey ? t(fieldLabelKey) : field;
        },
        getIssueMessage: (issue) => {
          const messageKey = getMeliDraftIssueMessageKey(issue);
          const fieldLabelKey = getMeliDraftIssueFieldLabelKey(issue.field);
          const fieldLabel = fieldLabelKey ? t(fieldLabelKey) : issue.field;
          return messageKey
            ? t(messageKey, getMeliDraftIssueMessageParams(issue, fieldLabel))
            : issue.message;
        },
        handleManage: (operation) => {
          setManagerPublicationId(null);
          setManagerOperation(operation);
        },
        handleManagePublication: (publicationId, operation) => {
          const publication = getMeliPublicationById(
            state.publications.publications,
            publicationId
          );
          const publicationOperation = getSupportedMeliOperationFromPublication(
            publication?.operation ?? operation
          );
          const fallbackOperation =
            publicationOperation ?? state.publications.operations[0];
          setManagerPublicationId(publicationId);
          setManagerOperation(fallbackOperation);
        },
        handlePublishShortcut: (operation) => {
          void state.shortcuts.handlePublishShortcut(
            operation,
            previews.draftParamsByOperation[operation] ?? null
          );
        },
        handleUnpublishShortcut: (operation, publicationId) => {
          void state.shortcuts.handleUnpublishShortcut(
            operation,
            publicationId
          );
        },
        isMeliSourcedProperty: state.connection.isMeliSourcedProperty,
        isMeliSyncMode: state.connection.isMeliSyncMode,
        loadingDraftByOperation: previews.loadingDraftByOperation,
        operations: state.publications.operations,
        portalConnectionStates: {
          [PropertyPortal.MELI]: {
            hasConnection: state.connection.hasMeliConnection,
            isConnected: state.connection.isMeliConnected,
          },
        },
        propertyIsActive: property?.isActive === true,
        propertyHasAssignee: property?.userId != null,
        publications: state.publications.publications,
        queuedAction: state.queued.action,
        t,
      }),
    [
      previews.draftByOperation,
      previews.draftParamsByOperation,
      previews.loadingDraftByOperation,
      canDeletePublications,
      canManagePublications,
      canReadPublications,
      property?.userId,
      setManagerPublicationId,
      state.connection.isMeliSourcedProperty,
      state.connection.hasMeliConnection,
      state.connection.isMeliConnected,
      state.connection.isMeliSyncMode,
      property?.isActive,
      state.publications.operations,
      state.publications.publications,
      state.queued.action,
      state.shortcuts,
      setManagerOperation,
      t,
    ]
  );

  const currentManagerPublication =
    managerOperation != null
      ? getMeliPublicationById(
          state.publications.publications,
          managerPublicationId
        ) ??
        state.publications.publications.find(
          (publication) =>
            getSupportedMeliOperationFromPublication(publication.operation) ===
              managerOperation && publication.portal === PropertyPortal.MELI
        ) ??
        null
      : null;

  return {
    connection: state.connection,
    items,
    loading: state.loading,
    modal: {
      currentManagerPublication,
      managerOperation,
      managerPublicationId,
      setManagerOperation,
      setManagerPublicationId,
    },
    permissions: {
      canDeletePublications,
      canManagePublications,
      canRepairSync:
        canRepairSourceSync &&
        state.connection.isSourceIntegrationConnected &&
        state.connection.sourceProviderId === IntegrationProviderId.MELI &&
        state.connection.sourceIntegrationHealthStatus !==
          IntegrationConnectionHealthStatus.ERROR,
      canReadPublications,
      loadingPermissions:
        loadingPublicationRead ||
        loadingPublishAll ||
        loadingPublishScoped ||
        loadingRepairSourceSyncPermission,
    },
    publications: state.publications,
    queued: state.queued,
    shortcuts: state.shortcuts,
    repairSync: state.repairSync,
    getQueuedDisplayStatus,
  };
};
